/**
 * <pvz-ship-date-guard>
 *
 * 発送予定日が違う商品を一緒にカートへ入れさせないためのガード。
 *
 * 商品にはカート追加時に発送日をラインアイテムプロパティ (_ship_date) で持たせている。
 * 追加しようとしている商品の発送日と、カート内の商品の発送日が 1 つでも違えば
 * 送信を止めて注意ポップアップを出す。
 *
 * - 未来日が入っていない商品 (すぐ発送) の発送日は空文字。空同士は一緒に入れられる。
 * - カートが空のときは常に通す。
 * - カート取得に失敗したときは止めない (買えなくなる方が困るため)。
 *
 * 限定販売の 1 点制限もここで見る (data-limit-product に商品 ID があるとき)。
 * 同じ商品 (サイズ違いを含む) がすでにカートにあれば止めて、data-limit-message の文言を出す。
 *
 * 送信の横取りは document の capture フェーズで行う。
 * フォーム自身に付いている <pvz-cart-added> のリスナーより確実に先に走らせるため。
 *
 * Markup:
 *   <pvz-ship-date-guard data-form="cpd-form" data-ship-date="2027-06-30" hidden>
 *     <div class="pvz-sdg__scrim" data-pvz-sdg-close></div>
 *     <div class="pvz-sdg__box">…<button data-pvz-sdg-close>閉じる</button></div>
 *   </pvz-ship-date-guard>
 */
if (!customElements.get('pvz-ship-date-guard')) {
  customElements.define(
    'pvz-ship-date-guard',
    class PerverzeShipDateGuard extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.form || 'cpd-form');
        if (!this.form) return;

        this.shipDate = this.dataset.shipDate || '';
        this.limitProduct = Number(this.dataset.limitProduct || 0);
        this.textEl = this.querySelector('.pvz-sdg__text');
        this.defaultText = this.textEl ? this.textEl.textContent : '';
        // 検証を通したあとの再送信は素通しする
        this.passThrough = false;

        this.onSubmit = this.onSubmit.bind(this);
        this.close = this.close.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        document.addEventListener('submit', this.onSubmit, true);
        this.querySelectorAll('[data-pvz-sdg-close]').forEach((el) => el.addEventListener('click', this.close));
        document.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        document.removeEventListener('keydown', this.onKeydown);
      }

      onKeydown(event) {
        if (event.key === 'Escape' && !this.hidden) this.close();
      }

      onSubmit(event) {
        if (event.target !== this.form) return;

        if (this.passThrough) {
          this.passThrough = false;
          return;
        }

        // 判定はカートの取得を挟むので非同期。いったん止めてから結果に応じて送り直す。
        event.preventDefault();
        event.stopImmediatePropagation();

        const button = this.form.querySelector('[name="add"]');
        if (button) button.setAttribute('aria-busy', 'true');

        this.check()
          .then((reason) => {
            if (!reason) {
              this.passThrough = true;
              this.resubmit();
            } else {
              // 止めた理由に合わせて文言を切り替える (発送日違い / 1 点制限)
              if (this.textEl) {
                this.textEl.textContent = reason === 'limit' ? this.dataset.limitMessage : this.defaultText;
              }
              this.open();
            }
          })
          .finally(() => {
            if (button) button.removeAttribute('aria-busy');
          });
      }

      /** 検証を通したあと、通常の送信フロー (pvz-cart-added) に戻す */
      resubmit() {
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
          return;
        }
        // requestSubmit が無い環境向け。pvz-cart-added が submit を拾って処理する。
        this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }

      /** 止める理由を返す ('limit' = 1 点制限 / 'ship-date' = 発送日違い / null = 追加してよい) */
      async check() {
        try {
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) return null;

          const cart = await response.json();
          if (!cart.items || cart.items.length === 0) return null;

          if (this.limitProduct && cart.items.some((item) => item.product_id === this.limitProduct)) return 'limit';

          const sameShipDate = cart.items.every((item) => {
            const key = (item.properties && item.properties._ship_date) || '';
            return key === this.shipDate;
          });
          return sameShipDate ? null : 'ship-date';
        } catch (e) {
          // カートが読めないときは通す
          return null;
        }
      }

      open() {
        this.hidden = false;
        requestAnimationFrame(() => this.classList.add('is-open'));
        const closeBtn = this.querySelector('[data-pvz-sdg-close]:not(.pvz-sdg__scrim)');
        if (closeBtn) closeBtn.focus();
      }

      close() {
        this.classList.remove('is-open');
        setTimeout(() => {
          this.hidden = true;
        }, 200);
      }
    }
  );
}
